import { useCallback } from 'react';
import { useKeycloak } from '@react-keycloak/web';

export interface MatterMigrationDocumentInput {
  externalDocumentId?: string;
  title?: string;
  description?: string;
  originalName?: string;
  mimeType?: string;
  filePath?: string;
  base64Content?: string;
  contentText?: string;
}

export interface MatterMigrationRowInput {
  externalClientId?: string;
  externalMatterId?: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  clientAddress?: string;
  matterNumber: string;
  matterDescription?: string;
  practiceArea?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
  stage?: 'intake' | 'review' | 'approved' | 'active' | 'closed' | 'rejected';
  documents?: MatterMigrationDocumentInput[];
}

export interface MatterMigrationOptions {
  preserveMatterNumbers: boolean;
  onExistingMatter: 'reuse' | 'skip' | 'error';
  onExistingDocument: 'rename' | 'skip' | 'error';
  continueOnError: boolean;
}

export interface MatterMigrationManifestRequest {
  sourceSystem: string;
  rows: MatterMigrationRowInput[];
  options?: Partial<MatterMigrationOptions>;
}

export interface MatterMigrationSummary {
  totalRows: number;
  validatedRows: number;
  failedRows: number;
  importedRows: number;
  skippedRows: number;
  importedClients: number;
  importedMatters: number;
  importedDocuments: number;
}

export interface MatterMigrationJob {
  migration_job_id: string;
  realm_id: string;
  source_system: string;
  created_by_dms_user_id?: string | null;
  created_by_keycloak_sub?: string | null;
  status:
    | 'draft'
    | 'dry_run_completed'
    | 'queued'
    | 'running'
    | 'completed'
    | 'completed_with_errors'
    | 'failed'
    | 'cancelled';
  options: Record<string, unknown>;
  summary?: MatterMigrationSummary | null;
  queue_job_id?: string | null;
  error_message?: string | null;
  started_at?: string | null;
  completed_at?: string | null;
  created_at: string;
  updated_at: string;
}

export interface MatterMigrationRow {
  migration_row_id: string;
  migration_job_id: string;
  row_number: number;
  external_client_id?: string | null;
  external_matter_id?: string | null;
  matter_number?: string | null;
  status: 'pending' | 'validated' | 'imported' | 'skipped' | 'failed';
  message?: string | null;
  client_id?: string | null;
  matter_id?: string | null;
  payload?: Record<string, unknown> | null;
  result?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

const API_BASE = '/api/v1/admin/firm-settings/matter-migration';

export const useMatterMigration = () => {
  const { keycloak } = useKeycloak();

  const getToken = useCallback((): string => {
    return keycloak?.token || '';
  }, [keycloak]);

  const getHeaders = useCallback((): HeadersInit => {
    return {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken()}`,
    };
  }, [getToken]);

  const parseJson = async <T>(response: Response): Promise<T> => {
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || `Request failed (${response.status})`);
    }
    return response.json();
  };

  const createDryRun = useCallback(
    async (request: MatterMigrationManifestRequest): Promise<{
      job: MatterMigrationJob;
      summary: MatterMigrationSummary;
      rows: MatterMigrationRow[];
    }> => {
      const response = await fetch(`${API_BASE}/dry-run`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(request),
      });
      return parseJson(response);
    },
    [getHeaders]
  );

  const createImportJob = useCallback(
    async (
      request: MatterMigrationManifestRequest,
      queueExecution: boolean = true
    ): Promise<{
      job: MatterMigrationJob;
      summary: MatterMigrationSummary;
      queueJobId?: string | null;
    }> => {
      const response = await fetch(`${API_BASE}/import`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          ...request,
          queueExecution,
        }),
      });
      return parseJson(response);
    },
    [getHeaders]
  );

  const executeImportJob = useCallback(
    async (migrationJobId: string): Promise<{ job: MatterMigrationJob }> => {
      const response = await fetch(`${API_BASE}/jobs/${migrationJobId}/execute`, {
        method: 'POST',
        headers: getHeaders(),
      });
      return parseJson(response);
    },
    [getHeaders]
  );

  const listJobs = useCallback(
    async (limit: number = 25): Promise<{ jobs: MatterMigrationJob[] }> => {
      const response = await fetch(`${API_BASE}/jobs?limit=${limit}`, {
        method: 'GET',
        headers: getHeaders(),
      });
      return parseJson(response);
    },
    [getHeaders]
  );

  const getJob = useCallback(
    async (migrationJobId: string): Promise<{ job: MatterMigrationJob }> => {
      const response = await fetch(`${API_BASE}/jobs/${migrationJobId}`, {
        method: 'GET',
        headers: getHeaders(),
      });
      return parseJson(response);
    },
    [getHeaders]
  );

  const getJobRows = useCallback(
    async (
      migrationJobId: string,
      params?: { status?: MatterMigrationRow['status']; limit?: number; offset?: number }
    ): Promise<{ rows: MatterMigrationRow[]; total: number; limit: number; offset: number }> => {
      const search = new URLSearchParams();
      if (params?.status) search.set('status', params.status);
      if (typeof params?.limit === 'number') search.set('limit', String(params.limit));
      if (typeof params?.offset === 'number') search.set('offset', String(params.offset));

      const response = await fetch(`${API_BASE}/jobs/${migrationJobId}/rows?${search.toString()}`, {
        method: 'GET',
        headers: getHeaders(),
      });
      return parseJson(response);
    },
    [getHeaders]
  );

  return {
    createDryRun,
    createImportJob,
    executeImportJob,
    listJobs,
    getJob,
    getJobRows,
  };
};
